/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-unused-vars */
import { useMemo, useState } from 'react'
import {
  endOfMonth,
  endOfYear,
  eachDayOfInterval,
  format,
  startOfYear,
} from 'date-fns'
import { Button } from '@/components/ui/button'
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '@/components/ui/select'
import {
  useBillsSummary,
  useReceivablesEOM,
  useBillsAging,
  usePaymentsSummary,
} from '@/hooks/useAnalytics'
import { useTheme } from '@/components/theme-provider'
import { formatCFA, formatDateFR } from '@/lib/utils'
import BilanVentes from './BilanVentes'
import type { BarDatumNV } from '@/types/backend'
import BilanRecevables from './BilanRecevables'
import BilanTopClients from './BilanTopClients'
import BilanCategories from './BilanCategories'
import BilanAging from './BilanAging'
import BilanEncaissementsJournalier from './BilanEncaissementsJournalier'
import {
  BUCKET_ORDER,
  type BucketLabel,
  type AgingBarDatum,
  type BillsAgingResponse,
} from '@/types/backend'
import type { KpiCardProps } from './KpiCard'
import KpiCard from './KpiCard'
import { useExpensesSummaryQuery } from '@/hooks/useExpenses'
import { useCashSummaryQuery } from '@/hooks/useCash'
import BilanDepenses from './BilanDepenses'
import BilanCash from './BilanCash'

const monthLabel = (m: number) =>
  [
    'Jan',
    'Fév',
    'Mar',
    'Avr',
    'Mai',
    'Juin',
    'Juil',
    'Août',
    'Sep',
    'Oct',
    'Nov',
    'Déc',
  ][Math.max(0, Math.min(11, m - 1))]

const ymLabel = (ym: string) => {
  const [_y, m] = ym.split('-').map(Number)
  return monthLabel(m)
}

const AGING_COLOR_MAP: Record<BucketLabel, string> = {
  Current: '#22c55e',
  '1-30': '#84cc16',
  '31-60': '#f59e0b',
  '61-90': '#fb7185',
  '90+': '#ef4444',
}

function buildAgingKeys(aging?: BillsAgingResponse): BucketLabel[] {
  const labels = new Set((aging?.buckets ?? []).map((b) => b.label))
  return BUCKET_ORDER.filter((l) => labels.has(l))
}

function buildAgingBarData(
  keys: BucketLabel[],
  aging?: BillsAgingResponse,
): AgingBarDatum[] {
  const row: AgingBarDatum = { tranche: 'Échéancier' }
  for (const k of keys) {
    const b = aging?.buckets?.find((bb) => bb.label === k)
    row[k] = b?.total ?? 0
  }
  return [row]
}

function buildAgingColors(keys: BucketLabel[]): string[] {
  return keys.map((k) => AGING_COLOR_MAP[k])
}

function buildCustomersByBucket(
  aging?: BillsAgingResponse,
):
  | Partial<
      Record<
        BucketLabel,
        { customerId: string; name: string; amount: number }[]
      >
    >
  | undefined {
  const hasBucketCustomers = (aging?.buckets ?? []).some(
    (b) => (b as any).customers?.length,
  )
  if (hasBucketCustomers) {
    const map: Partial<
      Record<
        BucketLabel,
        { customerId: string; name: string; amount: number }[]
      >
    > = {}
    for (const b of aging!.buckets) {
      const list = (b as any).customers as
        | { customerId: string; name: string; amount: number }[]
        | undefined
      if (list?.length) map[b.label as BucketLabel] = list
    }
    return map
  }
  if (aging?.customers?.length) {
    return {
      Current: aging.customers
        .filter((c) => (c.open ?? 0) > 0)
        .map((c) => ({
          customerId: c.customerId,
          name: c.name,
          amount: c.open,
        })),
    }
  }
  return undefined
}

export default function BilansPage() {
  const now = new Date()
  const currentYear = now.getFullYear()
  const [year, setYear] = useState<number>(currentYear)
  const [asOf, setAsOf] = useState(format(new Date(), 'yyyy-MM-dd'))
  const [dailyMonth, setDailyMonth] = useState<number>(now.getMonth() + 1)

  const from = startOfYear(new Date(year, 0, 1))
  const to = endOfYear(new Date(year, 0, 1))
  // Bornes "to" côté API : exclusives (documenté back-end), donc on envoie le
  // 1er janvier de l'année suivante plutôt que le 31/12 pour ne pas exclure
  // la dernière journée de l'année des totaux.
  const toExclusive = startOfYear(new Date(year + 1, 0, 1))
  const fromISO = format(from, 'yyyy-MM-dd')
  const toISO = format(to, 'yyyy-MM-dd')
  const toApiISO = format(toExclusive, 'yyyy-MM-dd')

  const {
    data: summary,
    isError: summaryError,
  } = useBillsSummary(year, true)
  const { data: receo, isError: receoError } = useReceivablesEOM(year, true)
  const { data: aging, isError: agingError } = useBillsAging(asOf, 30, true)
  const {
    data: pay,
    isError: payError,
  } = usePaymentsSummary(fromISO, toApiISO, true)
  const { data: exp, isError: expError } = useExpensesSummaryQuery(
    fromISO,
    toApiISO
  )
  const { data: cash, isError: cashError } = useCashSummaryQuery(
    fromISO,
    toApiISO
  )

  // ✅ Ces requêtes retombent silencieusement sur `?? 0` en cas d'échec, ce
  // qui peut laisser croire à tort qu'il n'y a aucune activité. On affiche
  // un bandeau d'erreur explicite au lieu de masquer le problème.
  const hasLoadError =
    summaryError ||
    receoError ||
    agingError ||
    payError ||
    expError ||
    cashError

  const { theme } = useTheme()
  const gridColor = theme === 'dark' ? '#374151' : '#f3f4f6'
  const gridOpacity = theme === 'dark' ? 0.4 : 1

  const kpi = useMemo(() => {
    return {
      orders: summary?.orders ?? 0,
      sales: summary?.totalSales ?? 0,
      cashIn: pay?.totals.in ?? 0,
      cashOut: Math.abs(pay?.totals.out ?? 0),
      cashNet: pay?.totals.net ?? 0,
      receivablesOpen: aging?.grandTotalBrut ?? 0,
    }
  }, [summary, pay, aging])

  const salesMonthly = (summary?.monthly ?? []).map((p) => ({
    x: monthLabel(p.month),
    y: p.total,
  }))

  const receivablesMonthly = (receo?.monthly ?? []).map((p) => ({
    x: ymLabel(p.ym),
    y: p.total,
  }))

  const topCustomers = (summary?.salesByCustomer ?? []).map((c) => ({
    name: c.name,
    value: c.total,
  }))

  const qtyByCategory = (summary?.quantityByCategory ?? []).map((c) => ({
    id: c.category ?? 'Sans catégorie',
    label: c.category ?? 'Sans catégorie',
    value: c.qty,
  }))

  const expensesMonthly: BarDatumNV[] = (exp?.monthly ?? []).map((m) => ({
    name: m.ym.split('-')[1],
    value: m.total,
  }))

  const cashMonthly = (cash?.monthly ?? []).map((r) => ({
    name: r.ym.split('-')[1],
    in: r.in,
    outAbs: Math.abs(r.out),
    net: r.net,
  }))

  const dailyEncaissements: BarDatumNV[] = useMemo(() => {
    const monthStart = new Date(year, dailyMonth - 1, 1)
    const monthEnd = endOfMonth(monthStart)
    const totalsByDay = new Map((pay?.daily ?? []).map((d) => [d.day, d.total]))
    return eachDayOfInterval({ start: monthStart, end: monthEnd }).map((d) => ({
      name: format(d, 'dd'),
      value: totalsByDay.get(format(d, 'yyyy-MM-dd')) ?? 0,
    }))
  }, [pay, year, dailyMonth])

  const agingBarKeys = useMemo(() => buildAgingKeys(aging), [aging])
  const agingBarData = useMemo(
    () => buildAgingBarData(agingBarKeys, aging),
    [aging, agingBarKeys],
  )
  const agingColors = useMemo(
    () => buildAgingColors(agingBarKeys),
    [agingBarKeys],
  )
  const customersByBucket = useMemo(
    () => buildCustomersByBucket(aging),
    [aging],
  )

  const years = Array.from({ length: 6 }, (_, i) => currentYear - 3 + i)

  const periodLabel = `du ${fromISO.split('-').reverse().join('/')} au ${toISO
    .split('-')
    .reverse()
    .join('/')}`
  const asOfLabel = formatDateFR(aging?.asOf ?? asOf)

  type KpiItem = KpiCardProps & { id: number }

  const kpiCardsData: KpiItem[] = [
    {
      id: 1,
      title: 'Commandes',
      value: kpi.orders.toLocaleString('fr-FR'),
      description: `Nombre de factures émises en ${year}.`,
      tooltip:
        'Volume de documents émis sur l’année sélectionnée (hors factures annulées/VOID). ' +
        'C’est un compte d’éléments, pas un montant.',
    },
    {
      id: 2,
      title: 'Ventes (TTC)',
      value: formatCFA(kpi.sales),
      description: `Chiffre d’affaires TTC facturé en ${year}.`,
      tooltip:
        'Somme des montants TTC des factures émises (comptabilité d’engagement). ' +
        'Indépendant des encaissements réels.',
    },
    {
      id: 3,
      title: 'Recevables (ouvert)',
      value: formatCFA(kpi.receivablesOpen),
      description: `Total des créances non réglées au ${asOfLabel}.`,
      tooltip:
        'Montant brut encore dû par les clients à la date indiquée. ' +
        '“Brut” = avant déduction d’éventuels crédits/avoirs non imputés.',
    },
    {
      id: 4,
      title: 'Encaissements (IN)',
      value: formatCFA(kpi.cashIn),
      description: `Paiements entrants ${periodLabel}.`,
      tooltip:
        'Somme des flux de trésorerie entrants (paiements reçus) sur la période de l’année sélectionnée.',
    },
    {
      id: 5,
      title: 'Sorties (OUT)',
      value: formatCFA(kpi.cashOut),
      description: `Décaissements ${periodLabel}.`,
      tooltip:
        'Somme des flux de trésorerie sortants (ex. remboursements). ' +
        'La valeur source est négative mais affichée ici en absolu pour la lisibilité.',
    },
    {
      id: 6,
      title: 'Net encaissé',
      value: formatCFA(kpi.cashNet),
      description: `IN + OUT ${periodLabel}.`,
      tooltip:
        'Solde net de trésorerie sur la période (encaissements + décaissements). ' +
        'Différent du chiffre d’affaires, car basé sur les flux de cash.',
    },
  ]

  return (
    <div className='space-y-6'>
      {/* Header */}
      <div className='flex flex-wrap items-center justify-between gap-3'>
        <h3 className='text-2xl font-semibold'>Bilans</h3>

        <div className='flex items-center gap-2'>
          <Select
            value={String(year)}
            onValueChange={(v) => setYear(Number(v))}
          >
            <SelectTrigger className='w-[140px]'>
              <SelectValue placeholder='Année' />
            </SelectTrigger>
            <SelectContent>
              {years.map((y) => (
                <SelectItem key={y} value={String(y)}>
                  {y}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button variant='outline' onClick={() => setYear(currentYear)}>
            Cette année
          </Button>
        </div>
      </div>

      {hasLoadError ? (
        <div className='rounded-md border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900 dark:border-amber-900/40 dark:bg-amber-950/30 dark:text-amber-300'>
          Certaines données n'ont pas pu être chargées — les chiffres
          affichés ci-dessous peuvent être incomplets. Réessayez de
          rafraîchir la page.
        </div>
      ) : null}

      {/* KPI Cards */}
      <div className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4'>
        {kpiCardsData.map((card) => (
          <KpiCard
            key={card.id}
            title={card.title}
            value={card.value}
            description={card.description}
            tooltip={card.tooltip}
          />
        ))}
      </div>

      {/* Charts grid */}
      <div className='grid grid-cols-1 lg:grid-cols-2 gap-6'>
        {/* Encaissements journaliers */}
        <BilanEncaissementsJournalier
          data={dailyEncaissements}
          month={dailyMonth}
          year={year}
          onMonthChange={setDailyMonth}
          gridColor={gridColor}
          gridOpacity={gridOpacity}
        />
        {/* Ventes mensuelles */}
        <BilanVentes
          salesMonthly={salesMonthly}
          gridColor={gridColor}
          gridOpacity={gridOpacity}
        />

        {/* Trésorerie */}
        <BilanCash
          data={cashMonthly}
          gridColor={gridColor}
          gridOpacity={gridOpacity}
        />

        {/* Recevables fin de mois */}
        <BilanRecevables
          receivablesMonthly={receivablesMonthly}
          gridColor={gridColor}
          gridOpacity={gridOpacity}
        />

        {/* Top clients (bar horizontale) */}
        <BilanTopClients
          gridColor={gridColor}
          gridOpacity={gridOpacity}
          data={topCustomers}
        />

        {/* Répartition par catégories (pie) */}
        <BilanCategories data={qtyByCategory} />

        {/* Aging buckets (stack horizontal simple) */}
        <BilanAging
          aging={aging}
          asOf={asOf}
          setAsOf={setAsOf}
          gridColor={gridColor}
          gridOpacity={gridOpacity}
          agingBarData={agingBarData}
          agingBarKeys={agingBarKeys}
          agingColors={agingColors}
          customersByBucket={customersByBucket}
        />

        {/**Dépenses */}
        <BilanDepenses
          data={expensesMonthly}
          gridColor={gridColor}
          gridOpacity={gridOpacity}
        />
      </div>
    </div>
  )
}
